import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import { MAX_RADIUS_KM } from "@/src/components/GeoFilterPanel";
import type {
  EventOrder,
  EventSort,
  EventStatusFilter,
} from "@/src/pages/Events/events.type";
import {
  DEFAULT_RADIUS,
  isEventOrder,
  isEventSort,
  isEventStatusFilter,
} from "@/src/pages/Events/events.type";

export type EventFilters = {
  q: string;
  page: number;
  sort: EventSort;
  order: EventOrder;
  status: EventStatusFilter;
  /** Centre de recherche — les trois vont ensemble ou pas du tout. */
  lat: number | null;
  lng: number | null;
  radius: number;
  /**
   * Le lieu tel qu'il a été choisi (« Lyon, France »).
   *
   * ⚠️ **Il n'est pas envoyé au serveur**, qui ne connaît que des coordonnées.
   * Il vit dans l'URL pour deux raisons : réafficher le champ après un retour
   * arrière, et dire *où* l'on cherche — `45.76 / 4.83` ne se relit pas.
   */
  near: string;
};

function readNumber(value: string | null, min: number, max: number) {
  if (value === null) return null;

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < min || parsed > max) return null;

  return parsed;
}

/**
 * Les filtres de la liste, portés par **l'URL** — même parti que les comptes,
 * et pour la même raison : on ouvre une fiche depuis cette liste, et le retour
 * arrière du navigateur démonte l'écran. Un `useState` ramènerait la première
 * page d'une recherche vide après chaque événement consulté.
 *
 * ⚠️ Rien de ce qui vient de la barre d'adresse n'est cru : chaque valeur est
 * relue et bornée ici, jamais passée telle quelle à la requête.
 */
export function useEventFilters() {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<EventFilters>(() => {
    const lat = readNumber(params.get("lat"), -90, 90);
    const lng = readNumber(params.get("lng"), -180, 180);

    // Une latitude sans longitude est refusée par le serveur (400), et à juste
    // titre : une requête à moitié géographique rendrait une liste plus large
    // que demandé sans le dire. On la traite ici comme une absence de centre.
    const hasCenter = lat !== null && lng !== null;

    const sort = params.get("sort");
    const order = params.get("order");
    const status = params.get("status");

    return {
      q: params.get("q") ?? "",
      page: readNumber(params.get("page"), 1, 10_000) ?? 1,
      sort: isEventSort(sort) ? sort : "date",
      order: isEventOrder(order) ? order : "asc",
      status: isEventStatusFilter(status) ? status : "all",
      lat: hasCenter ? lat : null,
      lng: hasCenter ? lng : null,
      radius:
        readNumber(params.get("radius"), 1, MAX_RADIUS_KM) ?? DEFAULT_RADIUS,
      near: hasCenter ? (params.get("near") ?? "") : "",
    };
  }, [params]);

  /**
   * Change un ou plusieurs filtres, et **revient page 1** — sauf quand c'est la
   * page elle-même qu'on change. Sans cette remise à zéro, filtrer depuis la
   * page 4 afficherait la page 4 d'un résultat qui n'en compte qu'une, donc du
   * vide qui ressemble à « aucun événement ».
   */
  const update = useCallback(
    (patch: Partial<EventFilters>) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);

          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === "") next.delete(key);
            else next.set(key, String(value));
          }

          if (!("page" in patch)) next.delete("page");

          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return { filters, update };
}
